import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer'

declare global {
	interface Window {
		mapData?: MapData
	}
}

interface MapData {
	viewport: Viewport | null
	stores: MarkerData[]
}

interface Viewport {
	leftDown: {
		lat: number
		lng: number
	}
	rightTop: {
		lat: number
		lng: number
	}
}

interface MarkerData {
	lat: number
	lng: number
	id: number
	title: string
	content?: string
	includeInBounds?: boolean
}

export type PdGoogleMapCurrentLocation = {
	title: string
	lat: number
	lng: number
}

export class PdGoogleMap {
	private mapData: MapData
	private map: google.maps.Map
	private bounds: google.maps.LatLngBounds
	private infoWindow: google.maps.InfoWindow

	private markers: Map<number, google.maps.Marker> = new Map()
	private markerClusterer: MarkerClusterer

	public initialZoom: number = 17

	public currentLocation: google.maps.Marker | undefined

	private icon: google.maps.Icon
	private clusterIcon: google.maps.Icon
	private currentLocationIcon: google.maps.Icon

	public constructor(map: google.maps.Map) {
		this.map = map
		this.mapData = window.mapData || { stores: [], viewport: null }

		this.bounds = new google.maps.LatLngBounds()
		this.infoWindow = new google.maps.InfoWindow({
			maxWidth: 230
		})

		this.icon = {
			url: require('@/img/icon/marker@2x.png'),
			scaledSize: new google.maps.Size(41, 45)
		}

		this.clusterIcon = {
			url: require('@/img/icon/marker-cluster@2x.png'),
			scaledSize: new google.maps.Size(41, 45),
			labelOrigin: new google.maps.Point(20, 20)
		}

		this.currentLocationIcon = {
			url: require('@/img/icon/marker-current@2x.png'),
			scaledSize: new google.maps.Size(26, 30)
		}

		this.addMarkers()
		this.extendBoundsByViewport(this.mapData.viewport)

		this.markerClusterer = new MarkerClusterer({
			map: this.map,
			markers: Array.from(this.markers.values()),
			renderer: { render: this.clusterRenderer.bind(this) }
		})

		this.map.fitBounds(this.bounds)

		this.setInitialMaxZoom()
	}

	private setInitialMaxZoom(): void {
		google.maps.event.addListenerOnce(this.map, 'idle', () => {
			const currentZoom = this.map.getZoom()
			if (!currentZoom || currentZoom > this.initialZoom) {
				this.map.setZoom(this.initialZoom)
			}
		})
	}

	public setCurrentLocation(currentLocation: PdGoogleMapCurrentLocation): void {
		this.currentLocation?.setMap(null)
		delete this.currentLocation

		const position = new google.maps.LatLng(currentLocation.lat, currentLocation.lng)

		this.currentLocation = new google.maps.Marker({
			map: this.map,
			title: currentLocation.title,
			position: position,
			animation: google.maps.Animation.DROP,
			icon: this.currentLocationIcon
		})

		this.fitBoundsToNearestMarker()
	}

	public filterMarkers(visibleMarkers: number[]): void {
		this.markerClusterer.clearMarkers()

		this.markers.forEach((marker, index) => {
			marker.setVisible(visibleMarkers.includes(index))
		})

		const markers = this.getVisibleMarkers()

		this.map.fitBounds(this.getFilteredBounds(markers))
		this.markerClusterer.addMarkers(markers)
		this.setInitialMaxZoom()
	}

	private addMarkers(): void {
		//  By default, do not show markers if the clusterer is initialized
		const initialMap = this.mapData.stores.length > 1 ? null : this.map

		this.mapData.stores.forEach((markerData) => {
			const markerPosition = new google.maps.LatLng(markerData.lat, markerData.lng)
			const includeInBounds = markerData.includeInBounds ?? true

			const marker = new google.maps.Marker({
				map: initialMap,
				animation: google.maps.Animation.DROP,
				title: markerData.title,
				position: markerPosition,
				icon: this.icon
			})
			marker.set('includeInBounds', includeInBounds)

			this.bindMarkerInfoWindow(marker, markerData)

			this.markers.set(markerData.id, marker)

			if (includeInBounds) {
				this.bounds.extend(markerPosition)
			}
		})
	}

	private extendBoundsByViewport(viewport: Viewport | null): void {
		if (viewport === null) {
			return
		}

		this.bounds.extend(new google.maps.LatLng(viewport.leftDown.lat, viewport.leftDown.lng))
		this.bounds.extend(new google.maps.LatLng(viewport.rightTop.lat, viewport.rightTop.lng))
	}

	private getVisibleMarkers(): google.maps.Marker[] {
		return Array.from(this.markers.values()).filter((marker) => marker.getVisible())
	}

	private getFilteredBounds(markers: google.maps.Marker[]): google.maps.LatLngBounds {
		const bounds = this.getBoundsForMarkers(markers)

		if (!bounds.isEmpty()) {
			return bounds
		}

		const allMarkersBounds = this.getBoundsForMarkers(markers, false)

		return allMarkersBounds.isEmpty() ? this.bounds : allMarkersBounds
	}

	private getBoundsForMarkers(
		markers: google.maps.Marker[],
		respectIncludeInBounds: boolean = true
	): google.maps.LatLngBounds {
		const bounds = new google.maps.LatLngBounds()

		markers.forEach((marker) => {
			if (respectIncludeInBounds && !marker.get('includeInBounds')) {
				return
			}

			const position = marker.getPosition()

			if (position) {
				bounds.extend(position)
			}
		})

		return bounds
	}

	private bindMarkerInfoWindow(marker: google.maps.Marker, markerData: MarkerData): void {
		marker.addListener('click', () => {
			this.infoWindow.close()
			this.infoWindow.setOptions({
				headerContent: markerData.title,
				content: markerData.content,
				ariaLabel: markerData.title
			})
			this.infoWindow.open({ map: this.map, anchor: marker })
		})
	}

	private clusterRenderer(cluster: Cluster): google.maps.Marker {
		const position = cluster.position

		return new google.maps.Marker({
			icon: this.clusterIcon,
			label: {
				text: `${String(cluster.count)}×`,
				fontSize: '15px',
				fontWeight: 'bold',
				className: 'map-marker--cluster'
			},
			position,
			zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count
		})
	}

	private fitBoundsToNearestMarker(): void {
		const position = this.currentLocation?.getPosition()

		if (!position) {
			return
		}

		const bounds = new google.maps.LatLngBounds(position)
		const nearestMarker = this.findNearestMarker(position, true)
		const nearestMarkerPosition = nearestMarker?.getPosition()

		if (!nearestMarkerPosition) {
			return
		}

		bounds.extend(nearestMarkerPosition)

		this.map.fitBounds(bounds, 100)
	}

	private findNearestMarker(position: google.maps.LatLng, onlyVisible: boolean): google.maps.Marker | undefined {
		const lat = position.lat()
		const lng = position.lng()

		const R = 6371 // radius of earth in km
		const distances: number[] = []
		let nearest = -1

		this.markers.forEach((marker, index) => {
			if (onlyVisible && !marker.getVisible()) {
				return
			}

			const mPosition = marker.getPosition()

			if (!mPosition) {
				return
			}

			const mLat = mPosition.lat()
			const mLng = mPosition.lng()

			const dLat = this.rad(mLat - lat)
			const dLong = this.rad(mLng - lng)

			const a =
				Math.sin(dLat / 2) * Math.sin(dLat / 2) +
				Math.cos(this.rad(lat)) * Math.cos(this.rad(lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2)
			const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
			const d = R * c

			distances[index] = d
			if (nearest === -1 || d < distances[nearest]) {
				nearest = index
			}
		})

		return this.markers.get(nearest)
	}

	private rad(x: number): number {
		return (x * Math.PI) / 180
	}
}
